import axios from "axios";
import { buildApiUrl } from "./api";

const CACHE_TTL_MS = 30_000;
const earningsCache = new Map();
const STATUS_MESSAGES = {
    warming: "Cached earnings opportunities are still warming.",
    missing: "No upcoming earnings event is available right now.",
    stale: "Earnings event data is stale, so cached opportunities are temporarily unavailable.",
    unavailable: "Earnings opportunities are temporarily unavailable.",
};

function buildCacheKey(symbol, initialCapital, accountSource = "trader") {
    return `${String(symbol || "").toUpperCase()}::${Math.max(Number(initialCapital || 0), 5000)}::${accountSource}`;
}

function normalizeSymbol(symbol) {
    return String(symbol || "").toUpperCase().trim();
}

function normalizeStatus(status) {
    if (status === "cache_miss") return "warming";
    if (status === "warming" || status === "missing" || status === "stale" || status === "unavailable") {
        return status;
    }
    return "unavailable";
}

function buildStatusPayload(symbol, status, message, extra = {}) {
    const normalizedStatus = normalizeStatus(status);
    return {
        found: false,
        symbol: normalizeSymbol(symbol),
        status: normalizedStatus,
        message: message || STATUS_MESSAGES[normalizedStatus],
        opportunities: [],
        best_opportunity: null,
        earnings: null,
        ...extra,
    };
}

function normalizeEarningsPayload(symbol, data) {
    if (data?.found) {
        return {
            ...data,
            symbol: normalizeSymbol(data.symbol || symbol),
            status: data.status || "ok",
        };
    }
    return buildStatusPayload(symbol, data?.status, data?.message, {
        cache_status: data?.cache_status,
        authority_status: data?.authority_status,
        authority_reason: data?.authority_reason,
        earnings: data?.earnings ?? null,
    });
}

export function getEarningsOpportunityNotice(data, symbol) {
    if (!data || data.found) return null;
    const status = normalizeStatus(data.status);
    const normalizedSymbol = normalizeSymbol(symbol || data.symbol);
    if (status === "warming") {
        return `Cached earnings opportunities for ${normalizedSymbol} are still warming.`;
    }
    if (status === "stale") {
        return `Earnings event data for ${normalizedSymbol} is stale, so cached opportunities are temporarily unavailable.`;
    }
    if (status === "missing") {
        return `No upcoming earnings event is available for ${normalizedSymbol}.`;
    }
    return `Earnings opportunities for ${normalizedSymbol} are temporarily unavailable.`;
}

export function allowsManualEarningsDate(data) {
    return Boolean(data && !data.found && normalizeStatus(data.status) === "missing");
}

export async function fetchEarningsOpportunity(symbol, initialCapital, accountSource = "trader") {
    const cacheKey = buildCacheKey(symbol, initialCapital, accountSource);
    const cached = earningsCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
        return cached.data;
    }

    try {
        const { data } = await axios.get(
            buildApiUrl(`/earnings-opportunity/symbol/${symbol}`),
            {
                params: {
                    initial_capital: Math.max(Number(initialCapital || 0), 5000),
                },
                withCredentials: true,
            }
        );
        const resolvedData = normalizeEarningsPayload(symbol, data);
        earningsCache.set(cacheKey, {
            data: resolvedData,
            timestamp: Date.now(),
        });
        return resolvedData;
    } catch (err) {
        const errorMessage = err?.response?.data?.message || err?.response?.data?.error || STATUS_MESSAGES.unavailable;
        return buildStatusPayload(symbol, "unavailable", errorMessage);
    }
}

export function clearEarningsOpportunityCache(symbol, initialCapital, accountSource = "trader") {
    if (!symbol) {
        earningsCache.clear();
        return;
    }
    earningsCache.delete(buildCacheKey(symbol, initialCapital, accountSource));
}
